/**
 * Canonical SyNeural terminology and shared copy.
 * Headers, footers, cards and pages read from here so names and status
 * language cannot drift apart.
 */

export type ComponentId =
  | 'syneural'
  | 'skp'
  | 'hcf'
  | 'cji'
  | 'ncji'
  | 'noetivra'
  | 'ncds'
  | 'cognocracy'
  | 'cognarchism'
  | 'cognarchicState'

export type ComponentDefinition = {
  id: ComponentId
  name: string
  shortName?: string
  definition: string
  href?: string
}

export const componentDefinitions: Record<ComponentId, ComponentDefinition> = {
  syneural: {
    id: 'syneural',
    name: 'SyNeural',
    definition:
      'Develops connected knowledge infrastructure, human capability frameworks and interactive workspaces for putting knowledge into action.',
    href: '/about',
  },
  skp: {
    id: 'skp',
    name: 'Semantic Knowledge Platform',
    shortName: 'SKP',
    definition:
      'Connects and governs knowledge, sources, concepts, relationships and review history. AI-assisted proposals remain distinct from approved knowledge.',
    href: '/semantic-knowledge',
  },
  hcf: {
    id: 'hcf',
    name: 'Human Capability Framework',
    shortName: 'HCF',
    definition:
      'Structures assessment, human review, development and reassessment. It is the umbrella for CJI and NCJI methods and development pathways.',
    href: '/human-capability',
  },
  cji: {
    id: 'cji',
    name: 'Civic Judgment Index',
    shortName: 'CJI',
    definition:
      'Defines structured reasoning and judgment assessment methods within HCF, including tasks, criteria and interpretation guidance.',
    href: '/cji',
  },
  ncji: {
    id: 'ncji',
    name: 'Neurocognitive Civic Judgment Index',
    shortName: 'NCJI',
    definition:
      'Organizes civic judgment assessment and development around five pillars within HCF, connecting findings with support, practice and reassessment.',
    href: '/ncji',
  },
  noetivra: {
    id: 'noetivra',
    name: 'Noetivra',
    definition:
      'SyNeural’s modular software environment for connected knowledge, human development and institutional coordination, combining shared services with dedicated workspaces and configurable modules.',
    href: '/noetivra',
  },
  ncds: {
    id: 'ncds',
    name: 'National Capability and Development System',
    shortName: 'NCDS',
    definition:
      'The government solution for citizen management, development, services and institutional improvement.',
    href: '/government',
  },
  cognocracy: {
    id: 'cognocracy',
    name: 'Cognocracy',
    definition:
      'A named civic project connecting SyNeural’s technology with a broader institutional framework. NCDS is its associated government solution.',
    href: '/ecosystem/cognocracy',
  },
  cognarchism: {
    id: 'cognarchism',
    name: 'Cognarchism',
    definition:
      'The related philosophy. It is not a requirement for adopting SyNeural’s commercial products.',
  },
  cognarchicState: {
    id: 'cognarchicState',
    name: 'Cognarchic State',
    definition: 'The canonical name of the related state concept.',
  },
}

/** Relationship paragraph used on HCF, CJI and NCJI. */
export const frameworkRelationship =
  'HCF provides the overall assessment and development framework. CJI defines structured reasoning and judgment assessment methods. NCJI organizes civic judgment assessment and development around five pillars. SKP maintains their supporting knowledge and versions, while Noetivra provides the interactive workspaces.'

/** Replaces generic Noetivra "Coming soon" labels. */
export const noetivraAvailabilityLabel = 'Interactive demos available · Production platform in development'

/** Shared availability paragraph for Noetivra, Demo Center and solution pages. */
export const demoAvailability =
  'Explore seven interactive demonstrations using fictional records. The demos illustrate workflows and save changes in your browser. Production integrations, secure institutional accounts and validated assessment methods are separate development milestones.'

/** Assessment research status, used wherever assessment is described. */
export const assessmentResearchStatus =
  'The five-pillar architecture is our working model. Assessment instruments remain under development. Pilot studies are needed to establish reliability, validity, lasting improvement and transfer to real-world decisions.'

/** Replaces installation-first descriptions. */
export const deliveryFormat =
  'The current demonstrations run in a web browser. Production deployment options, integrations and any installable applications remain part of product development.'

/** Shared capabilities across domains (not extra domains). */
export const sharedCapabilitiesCopy =
  'Education, Talent, Leadership and Community demonstrations reuse a common development workflow, adapted to their participants and responsibilities. Leadership exercises can also be configured within employer, government, education and community programs. Collaboration connects authorized contributions and reviews across domains. Research and Clinical can use SKP without requiring CJI or NCJI assessment.'

/** Note placed beneath CJI's six domains. */
export const cjiScopeNote =
  'These describe CJI’s assessment scope. NCJI uses a separate five-pillar working architecture to organize civic judgment and development. The two descriptions overlap, but they are not interchangeable lists. Their task-level relationships will be specified and tested as the assessment methods develop.'

/** Explains how the HCF cycle and the NCJI pathway relate. */
export const cycleRelationship =
  'In the HCF cycle, Review includes explaining and checking findings, and Develop includes recommendations, practice and support. Advance, in the NCJI pathway, means moving toward another development goal when the evidence supports it.'

/** Aggregate views never describe individuals. */
export const aggregateNote =
  'Group patterns describe a population or institution. They do not establish any individual’s capabilities.'

/** Institutional outcomes are not attributed solely to individual judgment. */
export const institutionalConditionsNote =
  'Institutional outcomes also depend on resources, incentives, services and conditions. They are not attributed solely to individual judgment.'

/**
 * Research concepts: exploratory ideas whose viability is still under study.
 * Kept separate from program eligibility, portable credentials and
 * job-relevant candidate comparison, which are part of product development.
 */
export const researchConceptStatus =
  'Research concept: an exploratory idea whose viability, validity and appropriate use are still under study. None of these are part of the current demos or the production development plan.'

export const researchConcepts = [
  {
    title: 'Civic ranking',
    body: 'Whether any summary of assessed civic judgment could be meaningful, fair and appropriate to use. This is distinct from program eligibility workflows, which apply defined service rules with human review.',
  },
  {
    title: 'Universal leadership certification',
    body: 'Whether a single leadership standard could hold across roles and institutions. This is distinct from the portable leadership credentials in development, which record a specific scope, issuer, methods and review dates.',
  },
  {
    title: 'Emotion inference',
    body: 'What, if anything, physiological or behavioral signals can reliably say about emotional states, and where such use would be lawful and appropriate. This is separate from job-relevant candidate comparison, which does not use inferred emotions.',
  },
]
